// shared/src/oracle/holdings-registry.ts
// D1 access for `oracle_holdings_registry` (migration 0096) — DISCOVERED holdings.
//
// Row/Zod/writer/reader ship with the DDL in ONE commit (feedback_writer_ddl_lockstep).
//
// The problem this closes: HOLDINGS_TOKENS (holdings-tokens.ts) is a hardcoded array, so a
// token bought tomorrow is enumerated by account-worker and valued on the dash, yet the
// oracle never learns of it — no canonical price, and no coverage alert either, because the
// self-check asserts over the same list that omits it. account-worker pushes here instead.
//
// THE UNION RULE (scope-review BLOCK #1): consumers resolve registry ∪ HOLDINGS_TOKENS.
// HOLDINGS_TOKENS remains the curated floor; the registry only ADDS. `holdingsUnion` below
// is the ONLY sanctioned way to combine them — call it rather than re-deriving the union at
// each call site, where a registry-only slip would silently unprice a real position.

import { z } from 'zod';
import { HOLDINGS_TOKENS, type HoldingsToken } from './holdings-tokens.js';
import { CHAIN_LABEL, DEFAULT_CHAIN_ID } from '../chains.js';

export interface OracleHoldingsRegistryRow {
  readonly chain:      string;
  readonly contract:   string;
  readonly key:        string;
  readonly symbol:     string;
  readonly decimals:   number;
  readonly network:    string;
  readonly first_seen: string;
  readonly last_seen:  string;
}

export const OracleHoldingsRegistryRowSchema = z.object({
  chain:      z.string().min(1),
  // Lowercase-enforced at the SCHEMA because the contract IS half the registry's identity:
  // the primary key is (chain, contract), and SQLite compares TEXT case-SENSITIVELY, so a
  // checksummed variant of an address already present would insert a SECOND row for the same
  // token — two registry entries, two sets of observations, one asset.
  // (Note this is NOT about the vendors: both normalizeAlchemyByAddress and
  // normalizeCoingeckoByContract lowercase before matching, so a mixed-case row would still
  // find its own response. The duplicate-identity risk is the whole reason.)
  contract:   z.string().regex(/^0x[0-9a-f]{40}$/, 'must be a lowercase 0x-prefixed address'),
  // '$SYM' (mainnet) or '$SYM.LABEL' (chain-qualified, e.g. '$USDC.POLY').
  key:        z.string().regex(/^\$[A-Z0-9]+(\.[A-Z0-9]+)?$/, "must be '$SYM' or '$SYM.CHAINLABEL'"),
  symbol:     z.string().min(1),
  decimals:   z.number().int().min(0).max(36),
  network:    z.string().min(1),
  first_seen: z.string().min(1),
  last_seen:  z.string().min(1),
});

/** Minimal D1 surface (mirrors the sibling oracle stores — keeps this unit testable). */
export interface HoldingsRegistryD1 {
  prepare(sql: string): {
    bind(...values: unknown[]): {
      run(): Promise<unknown>;
      all<T>(): Promise<{ results?: T[] }>;
    };
  };
}

/** An enumerated token as account-worker knows it, before it is a row. */
export interface DiscoveredToken {
  readonly chain:    string;
  readonly contract: string;
  readonly symbol:   string;
  readonly decimals: number;
  readonly network:  string;
}

/** Canonical oracle key for a token on a chain.
 *
 *  Ethereum mainnet is the DEFAULT namespace: mainnet USDC is '$USDC', Polygon's is
 *  '$USDC.POLY' (operator decision 2026-07-15). Without the suffix the same symbol on two
 *  chains would collide on ONE key and one contract would silently overwrite the other's
 *  price — a holder of Polygon USDC would be shown mainnet USDC's number with no trace.
 *
 *  Mainnet stays UNSUFFIXED so the scheme only ever ADDS keys: all 4 curated
 *  HOLDINGS_TOKENS are mainnet and their keys are byte-identical before and after this.
 *
 *  The '$' prefix IS the crypto discriminator (shared/src/tickers.ts isCryptoTicker), which
 *  matches on the prefix alone — so a suffixed key still classifies as crypto and cannot
 *  land in the Stocks basket (the 2026-07-15 bug).
 *
 *  Throws on an unknown chain rather than inventing a suffix: a silent '$USDC' for an
 *  unmapped chain is exactly the collision this function exists to prevent
 *  (feedback_no_fallbacks). */
export function keyForToken(symbol: string, chain: string): string {
  const sym = symbol.toUpperCase();
  if (chain === DEFAULT_CHAIN_ID) return `$${sym}`;
  const label = CHAIN_LABEL[chain];
  if (!label) throw new Error(`keyForToken: unmapped chain '${chain}' — add it to shared/src/chains.ts`);
  return `$${sym}.${label}`;
}

const COLS = 'chain, contract, key, symbol, decimals, network, first_seen, last_seen';

/** Idempotent upsert keyed on the LOGICAL item (chain, contract) — never on the run that
 *  observed it (feedback_idempotency_identity_excludes_run). Re-enumeration refreshes
 *  last_seen; first_seen is written ONCE and never moves (COALESCE keeps the original), so
 *  "how long have I held this?" survives every later push. */
export async function upsertDiscovered(
  db:  HoldingsRegistryD1,
  rows: ReadonlyArray<OracleHoldingsRegistryRow>,
): Promise<void> {
  for (const r of rows) {
    await db
      .prepare(
        `INSERT INTO oracle_holdings_registry (${COLS})
         VALUES (?,?,?,?,?,?,?,?)
         ON CONFLICT(chain, contract) DO UPDATE SET
           key=excluded.key, symbol=excluded.symbol, decimals=excluded.decimals,
           network=excluded.network, last_seen=excluded.last_seen,
           first_seen=COALESCE(oracle_holdings_registry.first_seen, excluded.first_seen)`,
      )
      .bind(r.chain, r.contract, r.key, r.symbol, r.decimals, r.network, r.first_seen, r.last_seen)
      .run();
  }
}

export function discoveredToRow(t: DiscoveredToken, nowIso: string): OracleHoldingsRegistryRow {
  return {
    chain:      t.chain,
    contract:   t.contract.toLowerCase(),
    key:        keyForToken(t.symbol, t.chain),
    symbol:     t.symbol.toUpperCase(),
    decimals:   t.decimals,
    network:    t.network,
    first_seen: nowIso,
    last_seen:  nowIso,
  };
}

/** Every registered holding. No staleness filter: a sold-to-zero token keeps its row and
 *  keeps being priced. Pricing a token nobody holds costs one batched Alchemy entry; NOT
 *  pricing one that IS held is a silent hole — the asymmetry says include it. */
export async function readRegistry(db: HoldingsRegistryD1): Promise<OracleHoldingsRegistryRow[]> {
  const res = await db
    .prepare(`SELECT ${COLS} FROM oracle_holdings_registry ORDER BY last_seen DESC`)
    .bind()
    .all<OracleHoldingsRegistryRow>();
  return res.results ?? [];
}

/** registry ∪ HOLDINGS_TOKENS — the ONLY sanctioned combination (see the header).
 *  Curated entries WIN on key collision: their contracts are hand-verified against the
 *  pools that price them, so a malformed/spoofed enumeration can never repoint a curated
 *  key at another contract. */
export function holdingsUnion(rows: ReadonlyArray<OracleHoldingsRegistryRow>): HoldingsToken[] {
  const byKey = new Map<string, HoldingsToken>();
  for (const r of rows) {
    byKey.set(r.key, { key: r.key, chain: r.chain, network: r.network, contract: r.contract });
  }
  for (const t of HOLDINGS_TOKENS) byKey.set(t.key, t);   // curated last → curated wins
  return [...byKey.values()];
}

// NO retention purge, deliberately (contrast the sibling oracle tables): this is a REGISTRY
// of size O(tokens held) — tens of rows — not a time series, so it exerts no D1 pressure.
// Ageing a row out by last_seen would silently UN-discover a token you still hold the moment
// a push lapsed (a fetcher outage, a paused cron), which is the exact silent-hole class this
// subsystem exists to close. Staleness here is a READ-side question, never a DELETE.
